import React, { useState } from 'react';
import { fortschritt } from '../../../services/sifa/teilnahmeLogic';
import { BESTAETIGUNG_TEXT } from '../../../services/sifa/datenschutzMuster';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { SignaturePad } from '../../common/SignaturePad';
import { useTeilnahme } from './useTeilnahme';
import {
  UNTERSCHRIFT_MAX_BREITE,
  frageNummerInFolge,
  gewaehlteAntworten,
  istTouchGeraet,
  type TeilnahmeModus,
} from './teilnahmeSeiteLogic';
import { datenschutzHref, sammelHref } from './routenLogic';
import { TeilnahmeRahmen } from './TeilnahmeRahmen';
import { TestlaufBanner, UebersichtRuecklink } from './SeitenKopf';
import { Begruessung } from './Begruessung';
import { AbschnittAnsicht } from './AbschnittAnsicht';
import { FragenAnsicht } from './FragenAnsicht';
import { FormularAnsicht } from './FormularAnsicht';
import { UnterschriftAnsicht } from './UnterschriftAnsicht';
import { ErgebnisAnsicht } from './ErgebnisAnsicht';
import { TestErgebnisAnsicht, TestKontingentAnsicht } from './TestErgebnisAnsicht';
import { FEHLERKASTEN, HINWEISKASTEN } from './teilnahmeSeiteStil';

/**
 * Oeffentliche Teilnehmerseite #/unterweisung/<linkToken> — und mit
 * `modus="test"` der Testlauf #/unterweisung-test/<token> (dieselben
 * Ansichten, Unterschiede in useTeilnahme: nichts wird gespeichert).
 *
 * Wird OHNE Login gerendert (index.tsx routet am Auth-Gate vorbei, wie
 * #/sign). Duenner Container: der Zustand lebt in useTeilnahme, die Regeln in
 * teilnahmeSeiteLogic/abschlussLogic; hier wird nur der aktuelle Schritt auf
 * die passende Ansicht abgebildet.
 *
 * `vonSammel` = Token des Sammel-Links, von dem der Teilnehmer kam
 * (`?von=…`): oben ein Ruecklink zur Liste, im Ergebnis „Zur Übersicht".
 */
export const UnterweisungTeilnahmePage: React.FC<{
  linkToken: string;
  modus?: TeilnahmeModus;
  vonSammel?: string | null;
}> = ({ linkToken, modus = 'echt', vonSammel = null }) => {
  const t = useTeilnahme(linkToken, modus);
  const test = modus === 'test';
  const schmal = useIsMobile();
  const [groberZeiger] = useState(() => {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  });
  const touch = istTouchGeraet(groberZeiger, schmal);
  // Im Testlauf gibt es keinen Sammel-Link — ein `von` waere dort ein Irrlaeufer.
  const uebersichtHref = !test && vonSammel ? sammelHref(vonSammel) : null;
  const oben = test ? (
    <TestlaufBanner gueltigBis={t.testGueltigBis} />
  ) : uebersichtHref ? (
    <UebersichtRuecklink href={uebersichtHref} />
  ) : undefined;

  if (t.ladeStatus === 'laedt') {
    return (
      <TeilnahmeRahmen oben={oben}>
        <p className="text-slate-500" aria-live="polite">
          {test ? 'Testlauf wird geladen…' : 'Unterweisung wird geladen…'}
        </p>
      </TeilnahmeRahmen>
    );
  }

  if (t.ladeStatus === 'ungueltig' || !t.unterweisung) {
    return (
      <TeilnahmeRahmen oben={oben}>
        {test ? (
          <>
            <h1 className="text-xl font-bold text-slate-900">Test-Link abgelaufen — im Editor neu erzeugen</h1>
            <div className={HINWEISKASTEN}>
              Ein Test-Link gilt 24 Stunden. Öffne die Unterweisung im Editor und tippe erneut auf „Testen", um einen
              neuen Link zu bekommen.
            </div>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-slate-900">Dieser Link ist nicht mehr gültig</h1>
            <div className={HINWEISKASTEN}>
              Die Unterweisung ist nicht aktiv oder der Link ist abgelaufen. Bitte wende dich an die Person, von der du
              den Link bekommen hast.
            </div>
          </>
        )}
      </TeilnahmeRahmen>
    );
  }

  if (t.ladeStatus === 'fehler') {
    return (
      <TeilnahmeRahmen oben={oben}>
        <h1 className="text-xl font-bold text-slate-900">Die Unterweisung konnte nicht geladen werden</h1>
        <div className={FEHLERKASTEN}>{t.ladeFehler}</div>
        <p className="text-slate-600">Bitte prüfe die Internetverbindung und lade die Seite neu.</p>
      </TeilnahmeRahmen>
    );
  }

  // Testlauf mit aufgebrauchtem Auswertungs-Kontingent: Klartext statt Ergebnis.
  if (test && t.testErschoepft) {
    return (
      <TeilnahmeRahmen oben={oben}>
        <TestKontingentAnsicht onNeuStarten={() => t.neuBeginnen()} />
      </TeilnahmeRahmen>
    );
  }

  const u = t.unterweisung;
  const { schritt, zustand } = t;

  // Smartphone: das Unterschriftenfeld ist die ganze Seite (wie SignaturePadPage) —
  // aber erst, wenn beide Haekchen gesetzt sind und „Jetzt unterschreiben" gedrueckt wurde.
  if (schritt.art === 'unterschrift' && touch && t.unterschriftOffen && t.abschlussFrei) {
    return (
      <SignaturePad
        titel={test ? 'Unterschrift — Testlauf' : 'Unterschrift — Unterweisung'}
        hinweis={
          test
            ? 'Testlauf: die Unterschrift wird nicht gespeichert.'
            : 'Bitte im Feld unterschreiben und übernehmen. Die Unterschrift wird auf deinem Nachweis abgedruckt.'
        }
        maxBreite={UNTERSCHRIFT_MAX_BREITE}
        uebernehmenLabel="Unterschrift übernehmen"
        busy={t.busy}
        fehler={t.fehler}
        onUebernehmen={(dataUrl) => void t.abschliessen(dataUrl, 'direkt')}
        onAbbrechen={t.unterschriftSchliessen}
        abbrechenLabel="Zurück"
      />
    );
  }

  const prozent = fortschritt(t.schrittIndex + 1, t.folge.length);
  // Fragen werden je Abschnitt gestellt (abschnittId) — gezaehlt wird die
  // Position in der Folge, nicht der Index in u.fragen.
  const frageNummer = frageNummerInFolge(t.folge, t.schrittIndex);
  const schrittText =
    schritt.art === 'abschnitt'
      ? `Abschnitt ${schritt.index + 1} von ${u.inhalt.length}`
      : schritt.art === 'frage'
        ? `Frage ${frageNummer} von ${u.fragen.length}`
        : undefined;

  let inhalt: React.ReactNode;
  switch (schritt.art) {
    case 'begruessung':
      inhalt = (
        <Begruessung
          unterweisung={u}
          busy={t.busy}
          fehler={t.fehler}
          neustartGrund={t.neustartGrund}
          test={test}
          onBeginnen={() => void t.beginnen()}
        />
      );
      break;
    case 'abschnitt': {
      const abschnitt = u.inhalt[schritt.index];
      inhalt = abschnitt ? (
        <AbschnittAnsicht
          key={abschnitt.id}
          abschnitt={abschnitt}
          nummer={schritt.index + 1}
          gesamt={u.inhalt.length}
          onWeiter={t.weiter}
          onZurueck={t.zurueck}
        />
      ) : null;
      break;
    }
    case 'frage': {
      const frage = u.fragen[schritt.index];
      inhalt = frage ? (
        <FragenAnsicht
          key={frage.id}
          frage={frage}
          nummer={frageNummer}
          gesamt={u.fragen.length}
          gewaehlt={gewaehlteAntworten(zustand.antworten, frage.id)}
          onUmschalten={(antwortId) => t.antwortWechseln(frage.id, antwortId)}
          onWeiter={t.weiter}
          onZurueck={t.zurueck}
        />
      ) : null;
      break;
    }
    case 'formular':
      inhalt = (
        <FormularAnsicht
          formular={zustand.formular}
          verstanden={zustand.verstanden}
          onAendern={t.formularAendern}
          onVerstanden={t.verstandenSetzen}
          onWeiter={t.weiter}
          onZurueck={t.zurueck}
        />
      );
      break;
    case 'unterschrift':
      inhalt = (
        <UnterschriftAnsicht
          teilnahmeToken={zustand.teilnahmeToken}
          busy={t.busy}
          fehler={t.fehler}
          // Derselbe Satz, den baueBestaetigung beim Abschluss speichert.
          bestaetigungText={BESTAETIGUNG_TEXT(u.titel)}
          datenschutzHref={datenschutzHref({ art: test ? 'test' : 'link', token: linkToken })}
          bestaetigt={t.bestaetigt}
          datenschutzGelesen={t.datenschutzGelesen}
          onBestaetigt={t.setBestaetigt}
          onDatenschutzGelesen={t.setDatenschutzGelesen}
          mitQr={!test}
          touch={touch}
          unterschriftOffen={t.unterschriftOffen}
          onUnterschriftOeffnen={t.unterschriftOeffnen}
          onUnterschriftSchliessen={t.unterschriftSchliessen}
          test={test}
          onUnterschrift={(dataUrl, weg) => void t.abschliessen(dataUrl, weg)}
          onZurueck={t.zurueck}
        />
      );
      break;
    case 'ergebnis':
      if (test) {
        inhalt = zustand.testErgebnis ? (
          <TestErgebnisAnsicht auswertung={zustand.testErgebnis} onNeuStarten={() => t.neuBeginnen()} />
        ) : null;
        break;
      }
      inhalt = zustand.ergebnis ? (
        <ErgebnisAnsicht
          ergebnis={zustand.ergebnis}
          teilnahmeToken={zustand.teilnahmeToken}
          email={zustand.formular.email}
          zertifikatStatus={t.zertifikatStatus}
          busy={t.busy}
          fehler={t.fehler}
          onZertifikatAnfordern={() => void t.zertifikatAnfordern()}
          onNochmal={() => void t.nochmalVersuchen()}
          onInhalteLesen={() => void t.inhalteNochmalLesen()}
          onNeuBeginnen={() => t.neuBeginnen()}
          uebersichtHref={uebersichtHref}
        />
      ) : null;
      break;
  }

  return (
    <TeilnahmeRahmen prozent={prozent} schrittText={schrittText} oben={oben}>
      {inhalt}
    </TeilnahmeRahmen>
  );
};
